import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as amplitude from "@amplitude/analytics-browser";
import { Header } from './Header';
import { PayMeCard } from '@app/dashboard/components/inner_layout/PayMeCard';
import { BranchLinksCard } from '@app/dashboard/components/inner_layout/PayMeCard/BranchLinksCard';
import { TransactionsHeader } from './TransactionsHeader';
import { TransactionsMetricsBox } from './TransactionsMetricsBox';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { PageStatus } from '@unified-checkout/design-system';
import { useTranslation } from 'react-i18next';
import { CircleExclamation } from '@unified-checkout/design-system/icons/index';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { usePaymentHandleConfig } from '@app/dashboard/utils/paymentHandleConfig';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { SkeletonBase } from '@unified-checkout/design-system';
import type {
  IColumnsMap,
  IFilterMap,
  IFiltersMap,
} from 'libs/design-system/src/types/data_table';
import type { ITransactionsDateRange } from '@app/dashboard/types/innerLayout/transactions';
import type { IPreDefinedRange } from 'libs/design-system/src/types/date_range_picker';
import { DataTable } from '@unified-checkout/design-system';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { useLangStore } from '@app/dashboard/store/lang';
import { getTransactionsFilters } from '@app/dashboard/services/inner_layout/transactions/transactions-filters';
import { getTransactions } from '@app/dashboard/services/inner_layout/transactions';
import { getCustomizationListing } from '@app/dashboard/services/inner_layout/checkout_customization/listing';
import { userInfo } from '@app/dashboard/utils/auth/userInfo';
import { getServerUrl } from '@app/dashboard/utils/serverUrls';
import { getCountryFromDomain } from '@app/dashboard/utils/getCountryFromDomain';
import { useIntegrationsStore } from '@app/dashboard/store/integrations';
import { getFromLocalStorage } from '@app/dashboard/utils/manageLocalStorage';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { COUNTRIES_INFO } from '@app/dashboard/constants/countries';
import { PERMISSIONS } from '@app/dashboard/constants/permissions';
import { isAccessible } from '@app/dashboard/utils/isAllowedPermission';
import { PAYME_EVENTS } from '@app/dashboard/constants/tracking_events/payme';
import styles from './payme.module.scss';

const TABLE_ID = 'payme_transaction_table';

const baseUrl = getServerUrl();

const handleImgPath = (path: string) => {
  const isLocalPath = !path?.includes('/epsilon/unified');

  return isLocalPath ? path : `${baseUrl}${path}`;
};

export const PayMe = () => {
  const { isLive } = useTestLiveModeStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const screenWidth = useScreenWidth();
  const { lang } = useLangStore();
  const queryClient = useQueryClient();
  const showTestModeMessage = !isLive;
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const canViewTransactions = isAccessible(PERMISSIONS.CAN_VIEW_TRANSACTIONS);

  const [isError, setIsError] = useState(false);
  const [dateRange, setDateRange] = useState<ITransactionsDateRange>({
    start_date: '',
    end_date: '',
  });
  const [appliedRangeText, setAppliedRangeText] =
    useState<IPreDefinedRange['label']>('');
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);

  const { currencies } = useIntegrationsStore();
  const currentMode = isLive ? 'live' : 'test';
  const country = getFromLocalStorage(LOCAL_STORAGE_KEYS.COUNTRY);
  const countryCurrency = COUNTRIES_INFO[country]?.currency;
  const availableCurrencies = currencies?.[currentMode] || [];

  const getDefaultCurrency = () => {
    if (!availableCurrencies.length) {
      return countryCurrency;
    }

    const currency = availableCurrencies.includes(countryCurrency) ?
      countryCurrency
      :
      availableCurrencies[0];

    return currency || null;
  };

  const [selectedCurrency, setSelectedCurrency] = useState(() => getDefaultCurrency());

  // Set default currency when currencies are loaded to prevent duplicate API calls
  useEffect(() => {
    if (!selectedCurrency && currencies?.[currentMode]?.length) {
      const defaultCurrency = getDefaultCurrency();
      if (defaultCurrency) {
        setSelectedCurrency(defaultCurrency);
      }
    }
  }, [currencies, currentMode]);

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GET_PAYMENT_HANDLE_CONFIG],
    });

    amplitude.track(PAYME_EVENTS.PAYME_VIEWED);
  }, []);

  const {
    data: paymentHandleConfig,
    isLoading: isLoadingConfig,
    isFetching: isFetchingConfig,
    isError: isErrorConfig,
    error: errorConfig,
  } = usePaymentHandleConfig({
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });

  // Fetch checkout customization data for logo
  const { data: checkoutCustomizationData } = useQuery({
    queryKey: [QUERY_KEYS.CUSTOMIZATION_LISTING, isLive],
    queryFn: () => getCustomizationListing(isLive).then((res) => res.data),
  });

  // Logo selection logic: Checkout Customization -> Business Branding -> Company Profile
  const merchantInfo = userInfo();
  const companyProfileLogo = merchantInfo.logo || getFromLocalStorage(LOCAL_STORAGE_KEYS.LOGO_URL) || null;
  const rawLogoUrl =
    checkoutCustomizationData?.merchant_logo ||
    companyProfileLogo;

  // Convert relative paths to absolute URLs
  const logoUrl = handleImgPath(rawLogoUrl);

  const isNotFoundError = useMemo(() => {
    if (!isErrorConfig || !errorConfig) return false;
    const error = errorConfig as any;
    const status = error?.response?.status;
    return status === 404;
  }, [isErrorConfig, errorConfig]);

  const handle = paymentHandleConfig?.handle_name || '';
  const branches = paymentHandleConfig?.branches || [];
  const isActivated = paymentHandleConfig?.is_activated ?? true;
  const canChangeHandleName =
    paymentHandleConfig?.can_change_handle_name ?? false;
  const hasHandle = Boolean(handle && handle.trim());
  const isLoadingPayMeContentAfterCreation = isFetchingConfig && !hasHandle;

  // Transactions table setup
  const columnsMap: IColumnsMap = [
    {
      id: 'amount',
      name: t('innerLayout.transactionsPage.table.headers.amount'),
      type: 'amount',
      key: 'amount',
      additionalText: {
        key: 'currency',
        placement: lang === 'en' ? 'start' : 'end',
      },
    },
    {
      id: 'status',
      name: t('innerLayout.transactionsPage.table.headers.status'),
      type: 'badge',
      key: 'status_text',
      labelComparingValue: 'status',
      label: {
        [`success`]: { color: 'green' },
        [`pending`]: { color: 'yellow' },
        [`failed`]: { color: 'red' },
        [`refunded`]: { color: 'grey' },
      },
    },
    {
      id: 'payment_method',
      name: t('innerLayout.transactionsPage.table.headers.method'),
      type: 'payment_method',
      key: 'payment_method',
    },
    {
      id: 'payment_type',
      name: t('innerLayout.transactionsPage.table.headers.type'),
      type: 'text',
      key: "trx_type"
    },
    {
      id: 'customer',
      name: t('innerLayout.transactionsPage.table.headers.customer'),
      type: 'text',
      key: 'customer_info.full_name',
      hintText: {
        conditionsType: 'location',
        conditions: {
          'customer_info.phone_number': {
            'customer_info.phone_number': /(.|\s)*\S(.|\s)*/,
          },
        },
        default: 'customer_info.email',
      },
    },
    {
      id: 'transaction_id',
      name: t('innerLayout.transactionsPage.table.headers.trnxId'),
      type: 'text',
      key: 'id',
    },
    {
      id: 'order_id',
      name: t('innerLayout.transactionsPage.table.headers.orderId'),
      type: 'text',
      key: 'order_id',
    },
    {
      id: 'date',
      name: t('innerLayout.transactionsPage.table.headers.dateCreated'),
      type: 'date',
      key: 'created_at',
      hintText: 'created_at',
    },
    {
      id: 'branch',
      name: t('innerLayout.transactionsPage.table.headers.branch'),
      type: 'text',
      key: 'branch_name',
      format: (fullData: any, value: any) => {
        return fullData.branch_name === 'Main PayMe Link'
          ? t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.mainPaymeLink')
          : (fullData.branch_name || '-')
      }
    },
  ];

  const {
    isLoading: isFiltersLoading,
    isError: isFiltersError,
    data: filtersResponse,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_TRANSACTIONS_FILTERS, isLive, lang],
    queryFn: () => getTransactionsFilters({ isLive, origin: "PayMe" }),
  });

  const filtersMap: IFiltersMap = useMemo(() => {
    return filtersResponse?.data?.data.map((filter: any) => {
      const {
        filter_type,
        filter_key,
        filter_name,
        filter_values,
        default_selection,
        filter_place_holder,
        filter_validation,
      } = filter;

      const filterType: Record<string, IFilterMap['type']> = {
        dropdown: 'select',
        multi_selection_dropdown: 'multi_selection',
        text_box: 'text',
        phone_number: 'phone',
      };

      return {
        type: filterType[filter_type],
        key: filter_key,
        name: filter_name,
        values: filter_values,
        defaultSelection: default_selection,
        placeHolder: filter_place_holder,
        validations: filter_validation,
      };
    });
  }, [filtersResponse?.data?.data]);

  const handleTableRowClick = (rowObj: any) => {
    rowObj &&
      navigate(INNER_ROUTES.PAYME_SINGLE_TRANSACTION.replace(':id', rowObj.id));
  };

  const queryKeys = useMemo(
    () => [
      QUERY_KEYS.GET_TRANSACTIONS,
      isLive,
      'PayMe',
      dateRange,
      selectedBranch,
      selectedCurrency,
    ],
    [isLive, dateRange, selectedBranch, selectedCurrency]
  );

  const queryFunctionParams = useMemo(() => {
    // Don't include params until both date range and currency are set to prevent duplicate API calls
    if (!dateRange.start_date || !dateRange.end_date || !selectedCurrency) {
      // Return object with empty string to fail DataTable's validation check
      return { __disabled: '' };
    }

    const params: Record<string, string | number | boolean> = {
      ...dateRange,
      origin: 'PayMe',
      currency: selectedCurrency,
    };

    if (selectedBranch) {
      params.branch = selectedBranch;
    }

    return params;
  }, [dateRange, selectedBranch, selectedCurrency]);

  const getPayMeTransactions = ({
    currentPage,
    pageSize,
    params = {},
  }: {
    currentPage: number;
    pageSize: number;
    params?: Record<string, string | number | boolean>;
  }) => {
    return getTransactions({
      currentPage,
      pageSize,
      isLive,
      params,
    });
  };

  const resetFiltersParams = useMemo(() => [isLive], [isLive]);

  if (showTestModeMessage) {
    return (
      <div className={styles['payme-wrapper']}>
        <Header />
        <div className={styles['payme-wrapper__page-status-container']}>
          <PageStatus
            status="warning"
            title={t('innerLayout.paymePage.testMode.title')}
            description={t('innerLayout.paymePage.testMode.description')}
            icon={CircleExclamation}
          />
        </div>
      </div>
    );
  }

  if (isLoadingConfig) {
    return (
      <div className={styles['payme-wrapper']}>
        <Header />
        <div className={styles['payme-wrapper__skeleton']}>
          <SkeletonBase height="11.3125rem" />
          <SkeletonBase height="15rem" />
          <SkeletonBase height="30rem" />
        </div>
      </div>
    );
  }

  if (isNotFoundError) {
    return (
      <div className={styles['payme-wrapper']}>
        <Header />
        <PayMeCard variant="handle" logoUrl={logoUrl} />
        {isLoadingPayMeContentAfterCreation && (
          <div className={styles['payme-wrapper__skeleton']}>
            <SkeletonBase height="15rem" />
            <SkeletonBase height="30rem" />
          </div>)}
      </div>
    );
  }

  if (isErrorConfig && !isNotFoundError) {
    return (
      <div className={styles['payme-wrapper']}>
        <Header />
        <div className={styles['payme-wrapper__page-status-container']}>
          <PageStatus
            status="error"
            title={t('innerLayout.paymePage.error.title')}
            description={t('innerLayout.paymePage.error.description')}
            icon={CircleExclamation}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={styles['payme-wrapper']}>
      <Header />
      <PayMeCard
        variant="handle"
        handle={handle}
        isActivated={isActivated}
        canChangeHandleName={canChangeHandleName}
        logoUrl={logoUrl}
      />
      {isLoadingPayMeContentAfterCreation && (
        <div className={styles['payme-wrapper__skeleton']}>
          <SkeletonBase height="15rem" />
          <SkeletonBase height="30rem" />
        </div>
      )}
      {hasHandle && (
        <>
          <BranchLinksCard
            handle={handle}
            branches={branches}
            isMainHandleActivated={isActivated}
            isPayMeConfigLoading={isLoadingConfig}
            logoUrl={logoUrl}
          />
          {canViewTransactions && (
            <>
              <TransactionsHeader
                setDateRange={setDateRange}
                setAppliedRangeText={setAppliedRangeText}
                selectedBranch={selectedBranch}
                setSelectedBranch={setSelectedBranch}
                selectedCurrency={selectedCurrency}
                setSelectedCurrency={setSelectedCurrency}
                branches={branches}
              />
              <TransactionsMetricsBox
                handle={handle}
                dateRange={dateRange}
                comparedRangeText={appliedRangeText}
                selectedBranch={{
                  id: selectedBranch,
                  name: selectedBranch === 'Main'
                    ? t('innerLayout.transactionsPage.singleTransactionsPage.detailsBoxes.details.mainBranch')
                    : branches.find((branch: any) => branch.id == selectedBranch)?.name
                }}
                selectedCurrency={selectedCurrency}
              />
              <DataTable
                lang={lang}
                filters={{
                  filtersMap: filtersMap,
                  isLoading: isFiltersLoading,
                  isError: isFiltersError,
                  isMobile: isMobile,
                  country: getCountryFromDomain(),
                }}
                tableBody={{
                  lang: lang,
                  tableId: TABLE_ID,
                  dataLocation: 'data.results',
                  columnsMap: columnsMap,
                  rowClickHandler: handleTableRowClick,
                }}
                pagination={{
                  hideLabels: isMobile,
                  type: 'cursor_based',
                  dataLocation: {
                    next: 'data.next',
                    prev: 'data.previous',
                  },
                  isMobile: isMobile,
                }}
                queries={{
                  queryKeys: queryKeys,
                  queryFunction: getPayMeTransactions,
                  queryFunctionParams: queryFunctionParams,
                  resetFiltersParams: resetFiltersParams,
                }}
                errorState={{
                  onError: (error) => setIsError(error),
                }}
                emptyState={{
                  text: t(
                    'innerLayout.transactionsPage.table.body.noTransactionsYet'
                  ),
                  description: t(
                    'innerLayout.transactionsPage.table.body.noTransactionsHint'
                  ),
                }}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};
